import type { WalkthroughStep } from "../types";

/**
 * CFG Builder's tour, started by a demo button or by asking for it.
 *
 * This tool's primary mode is an empty form that no folder is ever dropped on,
 * and a user in that mode has to ask for this tour before they see it — which
 * is the whole argument for why no field hint and no domain explanation was
 * moved out of the island and into a step here: moving it would have deleted it
 * for every user who never asks. Every step below restates something the island
 * still says for itself.
 *
 * The `welcome` step announces the sample and is dropped by the island when no
 * sample is loaded, because "not your files" is false over a folder the reader
 * picked themselves.
 *
 * Also reachable without loading a sample. The island renders a permanent
 * "Show me how this works" control in its intake row (`ShowTourButton`), which
 * raises the same signal, because gating every route to the tour on the demo
 * button left the tool's actual user — somebody who dropped their own folder —
 * with no tutorial and no way to ask for one. So a step may still assume a
 * result is on screen, and may no longer assume that result came from us:
 * anything that describes the sample by name says so explicitly rather than
 * relying on there being no other way in.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "This is a sample folder",
    body: "Four resources whose folder order does not boot are loaded — not your files. Five stops, then clear it and build your own.",
    placement: "center",
  },
  {
    id: "folder",
    title: "The folder decides the ensure block",
    body: "Dropped folders are read for what each resource declares it depends on, and the `ensure` block is written in dependency order rather than folder order. Cycles and missing dependencies are named in the findings, never quietly dropped.",
    selector: "[data-walkthrough='cfg-folder']",
    placement: "right",
  },
  {
    id: "order",
    title: "The order it will be started in",
    body: "This numbered list is what the generated file writes. The two resources that were sitting above the things they need have moved down it. If nothing could be ordered, this list says so rather than implying an order nobody computed.",
    selector: "[data-walkthrough='cfg-order']",
    placement: "right",
  },
  {
    id: "import",
    title: "It patches, it does not replace",
    body: "Paste a config you already run and the output becomes a patch of your file: comments, `exec` lines and every convar this tool has no opinion about come back byte for byte, and the form fills in from what your config already sets.",
    selector: "[data-walkthrough='cfg-import']",
    placement: "right",
  },
  {
    id: "output",
    title: "The file, and every line changed",
    body: "Above the file is what this tool altered about your input — a value clamped, a line reordered — with the reason. Nothing is changed silently, and a sample-pack config downloads with a sample- prefix so it cannot be mistaken for yours.",
    selector: "[data-walkthrough='cfg-output']",
    placement: "left",
  },
  {
    id: "findings",
    title: "What is wrong with it",
    body: "Eleven rules read the file the way a server would, and everything they could not check is listed underneath in words. A clean report is a static check of the files you gave it — it is not proof the server boots.",
    selector: "[data-walkthrough='cfg-findings']",
    placement: "left",
  },
];

export const finishLabel = "Build my own config";
